import React, { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { apiClient } from "../../services/apiClient";
import LoadingSpinner from "../common/LoadingSpinner";
import CompanyDocumentSelectionModal from "./CompanyDocumentSelectionModal";
import RFPDocumentSelectionModal from "./RFPDocumentSelectionModal";
import ModelContentSelectionModal from "./ModelContentSelectionModal";

const SectionChatWithArtifact = ({
  section,
  documentId,
  currentContent,
  onArtifactUpdate,
  onSessionChange
}) => {
  // State
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState("");
  const [currentSession, setCurrentSession] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);

  // Document state
  const [proposalDocuments, setProposalDocuments] = useState([]);
  const [sessionDocuments, setSessionDocuments] = useState([]);
  const [stagedDocuments, setStagedDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Company Document state
  const [stagedPastPerformance, setStagedPastPerformance] = useState([]);
  const [stagedResumes, setStagedResumes] = useState([]);
  const [ppModalOpen, setPPModalOpen] = useState(false);
  const [resumeModalOpen, setResumeModalOpen] = useState(false);
  const [rfpModalOpen, setRfpModalOpen] = useState(false);
  const [companyDocs, setCompanyDocs] = useState({ pastPerformance: [], resumes: [] });

  // Model Content state
  const [stagedModelContent, setStagedModelContent] = useState([]);
  const [modelContentModalOpen, setModelContentModalOpen] = useState(false);
  const [modelContentDocs, setModelContentDocs] = useState([]);

  // AI Model selection state
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState("");
  const [modelsLoading, setModelsLoading] = useState(true);

  // References
  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // Load available AI models on mount
  useEffect(() => {
    loadAvailableModels();
    loadCompanyDocuments();
    loadModelContentDocuments();
  }, []);

  // Initialize chat session when section changes
  useEffect(() => {
    if (section && documentId) {
      loadChatSession();
      loadProposalDocuments();
    }
  }, [section?.section_id, documentId]);

  // Notify parent of session changes
  useEffect(() => {
    if (onSessionChange) {
      onSessionChange(currentSession);
    }
  }, [currentSession, onSessionChange]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const loadAvailableModels = async () => {
    try {
      setModelsLoading(true);
      const response = await apiClient.getAIModels();

      if (response.success && response.data.models) {
        setAvailableModels(response.data.models);

        // Set default model (the one marked as isDefault)
        const defaultModel = response.data.models.find(m => m.isDefault);
        if (defaultModel) {
          setSelectedModel(defaultModel.model);
        }
      }
      setModelsLoading(false);
    } catch (error) {
      console.error('Failed to load AI models:', error);
      setModelsLoading(false);
    }
  };

  const loadChatSession = async () => {
    try {
      setIsLoading(true);

      const response = await apiClient.getCurrentSectionChatSession(
        section.section_id,
        documentId
      );

      if (response.success) {
        setCurrentSession(response.data.session);

        // Load session with messages
        const sessionResponse = await apiClient.getSectionChatSession(
          section.section_id,
          response.data.session.session_id
        );

        if (sessionResponse.success) {
          setMessages(sessionResponse.data.messages || []);
          loadSessionDocuments(response.data.session.session_id);
        }
      }
    } catch (error) {
      console.error("Failed to load chat session:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadProposalDocuments = async () => {
    try {
      console.log("🔍 Loading proposal documents for section:", section.section_id, "documentId:", documentId);
      const response = await apiClient.getProposalDocumentsForSection(
        section.section_id,
        documentId
      );

      console.log("📄 Proposal documents response:", response);

      if (response.success) {
        const docs = response.data.documents || [];
        console.log("✅ Setting proposal documents:", docs.length, "documents");
        setProposalDocuments(docs);
      }
    } catch (error) {
      console.error("❌ Failed to load proposal documents:", error);
    }
  };

  const loadSessionDocuments = async (sessionId) => {
    try {
      const response = await apiClient.getSectionChatDocuments(
        section.section_id,
        sessionId
      );

      if (response.success) {
        setSessionDocuments(response.data.documents || []);
      }
    } catch (error) {
      console.error("Failed to load session documents:", error);
    }
  };

  const loadCompanyDocuments = async () => {
    try {
      const [ppResponse, resumeResponse] = await Promise.all([
        apiClient.getCompanyDocuments('past_performance'),
        apiClient.getCompanyDocuments('resume')
      ]);

      setCompanyDocs({
        pastPerformance: (ppResponse.success ? ppResponse.data.documents : []).filter(
          doc => doc.processing_status === 'completed'
        ),
        resumes: (resumeResponse.success ? resumeResponse.data.documents : []).filter(
          doc => doc.processing_status === 'completed'
        )
      });
    } catch (error) {
      console.error("Failed to load company documents:", error);
    }
  };

  const loadModelContentDocuments = async () => {
    try {
      const response = await apiClient.getModelContentDocuments();
      if (response.success) {
        setModelContentDocs(
          (response.data.documents || []).filter(
            doc => doc.processing_status === 'completed'
          )
        );
      }
    } catch (error) {
      console.error("Failed to load model content documents:", error);
    }
  };

  const handleSendMessage = async () => {
    if (!messageInput.trim() || !currentSession || isSending) return;

    const userMessage = messageInput.trim();
    setMessageInput("");
    setIsSending(true);

    try {
      // Add user message to UI immediately
      const tempUserMessage = {
        role: "user",
        content: userMessage,
        message_id: Date.now(), // Temporary ID
        created_at: new Date().toISOString()
      };

      setMessages(prev => [...prev, tempUserMessage]);

      // Send to API with selected model, company documents, and model content
      const response = await apiClient.sendSectionChatMessage(
        section.section_id,
        currentSession.session_id,
        userMessage,
        currentContent,
        stagedDocuments,
        selectedModel,
        {
          pastPerformanceIds: stagedPastPerformance,
          resumeIds: stagedResumes,
          modelContentIds: stagedModelContent
        }
      );

      if (response.success) {
        console.log("API Response:", response.data);

        // Add AI response to messages
        const aiMessage = {
          role: "assistant",
          content: response.data.chatResponse || response.data.chat_response,
          artifact_content: response.data.artifactContent || response.data.artifact_content,
          artifact_action: response.data.artifactAction || response.data.artifact_action,
          message_id: response.data.messageId,
          created_at: response.data.timestamp
        };

        setMessages(prev => [...prev.slice(0, -1), tempUserMessage, aiMessage]);

        // Handle artifact updates
        if (response.data.artifactAction !== "none" && response.data.artifactContent) {
          onArtifactUpdate(
            response.data.artifactContent,
            response.data.artifactAction,
            response.data.messageId
          );
        }
      }
    } catch (error) {
      console.error("Failed to send message:", error);
      // Remove the temporary user message on error
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || !currentSession) return;

    try {
      setUploading(true);

      const response = await apiClient.uploadSectionChatDocument(
        section.section_id,
        currentSession.session_id,
        file
      );

      if (response.success) {
        await loadSessionDocuments(currentSession.session_id);
      }
    } catch (error) {
      console.error("Failed to upload document:", error);
    } finally {
      setUploading(false);
      event.target.value = ""; // Reset file input
    }
  };

  const handleRemoveDocument = async (documentId, isProposal = false) => {
    if (isProposal) {
      setStagedDocuments(prev => prev.filter(id => id !== documentId));
    } else {
      try {
        await apiClient.removeSectionChatDocument(
          section.section_id,
          currentSession.session_id,
          documentId
        );
        await loadSessionDocuments(currentSession.session_id);
      } catch (error) {
        console.error("Failed to remove document:", error);
      }
    }
  };

  const handleStageDocument = (documentId) => {
    if (!stagedDocuments.includes(documentId)) {
      setStagedDocuments(prev => [...prev, documentId]);
    }
  };

  // Helper function to truncate filename intelligently
  const truncateFilename = (filename, maxLength = 40) => {
    if (!filename || filename.length <= maxLength) return filename;

    // Try to preserve the file extension
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex > 0) {
      const name = filename.substring(0, lastDotIndex);
      const extension = filename.substring(lastDotIndex);

      // Calculate how much space we have for the name
      const availableLength = maxLength - extension.length - 3; // -3 for "..."

      if (availableLength > 10) {
        return name.substring(0, availableLength) + '...' + extension;
      }
    }

    // Fallback: simple truncation
    return filename.substring(0, maxLength - 3) + '...';
  };

  const renderMessage = (message, index) => {
    const isUser = message.role === "user";

    return (
      <div
        key={message.message_id || index}
        className={`flex ${isUser ? "justify-end" : "justify-start"} mb-4`}
      >
        <div
          className={`max-w-[80%] rounded-lg px-4 py-2 ${
            isUser
              ? "bg-primary-500 text-white"
              : "bg-gray-100 text-gray-900 border border-gray-200"
          }`}
        >
          <div className="whitespace-pre-wrap break-words">
            {typeof message.content === 'string'
              ? message.content
              : typeof message.content === 'object'
                ? (message.content.chat_response || JSON.stringify(message.content))
                : String(message.content)
            }
          </div>

          {/* Artifact indicator for AI messages */}
          {!isUser && message.artifact_action && message.artifact_action !== "none" && (
            <div className="mt-2 pt-2 border-t border-gray-200">
              <div className="flex items-center space-x-2 text-xs text-blue-600">
                <FontAwesomeIcon icon="magic" />
                <span>
                  {message.artifact_action === "create" ? "Created" : "Updated"} section content
                </span>
              </div>
            </div>
          )}

          <div className={`text-xs mt-1 ${isUser ? "text-blue-100" : "text-gray-500"}`}>
            {new Date(message.created_at).toLocaleTimeString()}
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <LoadingSpinner size="lg" text="Loading chat..." />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Chat Header */}
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-gray-900">AI Assistant</h3>
            <p className="text-sm text-gray-600">
              Discussing: {section.section_title}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {/* AI Model Selection */}
            {modelsLoading ? (
              <div className="text-xs text-gray-500">Loading models...</div>
            ) : (
              <select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                disabled={isSending}
                className="text-sm px-3 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                title="Select AI model for chat"
              >
                <option value="">Select Model</option>

                {/* OpenAI Models */}
                {availableModels.filter(m => m.provider === 'openai').length > 0 && (
                  <optgroup label="OpenAI">
                    {availableModels
                      .filter(m => m.provider === 'openai')
                      .map((model) => (
                        <option key={model.model} value={model.model}>
                          {model.name}
                        </option>
                      ))}
                  </optgroup>
                )}

                {/* Anthropic Models */}
                {availableModels.filter(m => m.provider === 'anthropic').length > 0 && (
                  <optgroup label="Anthropic">
                    {availableModels
                      .filter(m => m.provider === 'anthropic')
                      .map((model) => (
                        <option key={model.model} value={model.model}>
                          {model.name}
                        </option>
                      ))}
                  </optgroup>
                )}

                {/* Google Models */}
                {availableModels.filter(m => m.provider === 'google').length > 0 && (
                  <optgroup label="Google">
                    {availableModels
                      .filter(m => m.provider === 'google')
                      .map((model) => (
                        <option key={model.model} value={model.model}>
                          {model.name}
                        </option>
                      ))}
                  </optgroup>
                )}
              </select>
            )}
          </div>
        </div>
      </div>

      {/* Document Controls */}
      <div className="px-4 py-2 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            <span>Reference Documents:</span>
          </div>

          <div className="flex items-center space-x-1">
            {/* Upload Ad-hoc Document */}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="relative p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors disabled:opacity-50 group"
              title="Upload Document"
            >
              {uploading ? (
                <FontAwesomeIcon icon="spinner" className="animate-spin w-5 h-5" />
              ) : (
                <FontAwesomeIcon icon="upload" className="w-5 h-5" />
              )}
              {sessionDocuments.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {sessionDocuments.length}
                </span>
              )}
            </button>

            {/* Reference RFP Documents */}
            <button
              onClick={() => setRfpModalOpen(true)}
              className="relative p-2 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 transition-colors group"
              title="Reference RFP Documents"
            >
              <FontAwesomeIcon icon="file-alt" className="w-5 h-5" />
              {stagedDocuments.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-green-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {stagedDocuments.length}
                </span>
              )}
            </button>

            {/* Reference Past Performance */}
            <button
              onClick={() => setPPModalOpen(true)}
              className="relative p-2 rounded-lg bg-yellow-50 text-yellow-600 hover:bg-yellow-100 transition-colors group"
              title="Reference Past Performance"
            >
              <FontAwesomeIcon icon="trophy" className="w-5 h-5" />
              {stagedPastPerformance.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-yellow-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {stagedPastPerformance.length}
                </span>
              )}
            </button>

            {/* Reference Resumes */}
            <button
              onClick={() => setResumeModalOpen(true)}
              className="relative p-2 rounded-lg bg-purple-50 text-purple-600 hover:bg-purple-100 transition-colors group"
              title="Reference Resumes"
            >
              <FontAwesomeIcon icon="user-tie" className="w-5 h-5" />
              {stagedResumes.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-purple-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {stagedResumes.length}
                </span>
              )}
            </button>

            {/* Reference Model Content */}
            <button
              onClick={() => setModelContentModalOpen(true)}
              className="relative p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors group"
              title="Reference Model Content"
            >
              <FontAwesomeIcon icon="file-contract" className="w-5 h-5" />
              {stagedModelContent.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {stagedModelContent.length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Document List */}
        {(stagedDocuments.length > 0 || sessionDocuments.length > 0 || stagedPastPerformance.length > 0 || stagedResumes.length > 0 || stagedModelContent.length > 0) && (
          <div className="mt-2 space-y-1">
            {/* Staged Proposal Documents */}
            {stagedDocuments.map(docId => {
              const doc = proposalDocuments.find(d => d.document_id === docId);
              if (!doc) return null;
              const filename = doc.original_file_name || doc.original_filename;
              return (
                <div
                  key={`staged-${docId}`}
                  className="flex items-center justify-between text-xs bg-green-50 text-green-700 px-2 py-1 rounded"
                  title={filename} // Show full name on hover
                >
                  <span className="truncate flex-1 mr-2">{truncateFilename(filename, 35)}</span>
                  <button
                    onClick={() => handleRemoveDocument(docId, true)}
                    className="text-green-500 hover:text-green-700 flex-shrink-0"
                  >
                    <FontAwesomeIcon icon="times" />
                  </button>
                </div>
              );
            })}

            {/* Session Documents */}
            {sessionDocuments.map(doc => {
              const filename = doc.original_filename || doc.original_file_name;
              return (
                <div
                  key={`session-${doc.document_id}`}
                  className="flex items-center justify-between text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded"
                  title={filename} // Show full name on hover
                >
                  <span className="truncate flex-1 mr-2">{truncateFilename(filename, 35)}</span>
                  <button
                    onClick={() => handleRemoveDocument(doc.document_id, false)}
                    className="text-blue-500 hover:text-blue-700 flex-shrink-0"
                  >
                    <FontAwesomeIcon icon="times" />
                  </button>
                </div>
              );
            })}

            {/* Staged Past Performance Documents */}
            {stagedPastPerformance.map(docId => {
              const doc = companyDocs.pastPerformance.find(d => d.document_id === docId);
              if (!doc) return null;
              return (
                <div
                  key={`pp-${docId}`}
                  className="flex items-center justify-between text-xs bg-yellow-50 text-yellow-700 px-2 py-1 rounded"
                  title={doc.pp_project_name || doc.document_name}
                >
                  <span className="truncate flex-1 mr-2">
                    <FontAwesomeIcon icon="trophy" className="mr-1" />
                    {doc.pp_project_name || doc.document_name}
                  </span>
                  <button
                    onClick={() => setStagedPastPerformance(prev => prev.filter(id => id !== docId))}
                    className="text-yellow-500 hover:text-yellow-700 flex-shrink-0"
                  >
                    <FontAwesomeIcon icon="times" />
                  </button>
                </div>
              );
            })}

            {/* Staged Resumes */}
            {stagedResumes.map(docId => {
              const doc = companyDocs.resumes.find(d => d.document_id === docId);
              if (!doc) return null;
              return (
                <div
                  key={`resume-${docId}`}
                  className="flex items-center justify-between text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded"
                  title={doc.resume_person_name || doc.document_name}
                >
                  <span className="truncate flex-1 mr-2">
                    <FontAwesomeIcon icon="user-tie" className="mr-1" />
                    {doc.resume_person_name || doc.document_name}
                  </span>
                  <button
                    onClick={() => setStagedResumes(prev => prev.filter(id => id !== docId))}
                    className="text-purple-500 hover:text-purple-700 flex-shrink-0"
                  >
                    <FontAwesomeIcon icon="times" />
                  </button>
                </div>
              );
            })}

            {/* Staged Model Content */}
            {stagedModelContent.map(docId => {
              const doc = modelContentDocs.find(d => d.document_id === docId);
              if (!doc) return null;
              return (
                <div
                  key={`model-${docId}`}
                  className="flex items-center justify-between text-xs bg-red-50 text-red-700 px-2 py-1 rounded"
                  title={doc.document_name}
                >
                  <span className="truncate flex-1 mr-2">
                    <FontAwesomeIcon icon="file-contract" className="mr-1" />
                    {doc.document_name}
                  </span>
                  <button
                    onClick={() => setStagedModelContent(prev => prev.filter(id => id !== docId))}
                    className="text-red-500 hover:text-red-700 flex-shrink-0"
                  >
                    <FontAwesomeIcon icon="times" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4">
        {messages.length === 0 ? (
          <div className="text-center text-gray-500 mt-8">
            <FontAwesomeIcon icon="comments" className="text-4xl mb-4" />
            <p className="text-lg mb-2">Start a conversation</p>
            <p className="text-sm">
              Ask me to improve, expand, or modify "{section.section_title}"
            </p>
          </div>
        ) : (
          <>
            {messages.map(renderMessage)}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      <div className="p-4 border-t border-gray-200">
        <div className="flex space-x-2">
          <textarea
            ref={textareaRef}
            value={messageInput}
            onChange={(e) => setMessageInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={`Ask me to improve "${section.section_title}"...`}
            rows={2}
            className="flex-1 resize-none border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            disabled={isSending}
          />
          <button
            onClick={handleSendMessage}
            disabled={!messageInput.trim() || isSending}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSending ? (
              <FontAwesomeIcon icon="spinner" className="animate-spin" />
            ) : (
              <FontAwesomeIcon icon="paper-plane" />
            )}
          </button>
        </div>
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileUpload}
        className="hidden"
        accept=".pdf,.doc,.docx,.txt,.csv,.xls,.xlsx"
      />

      {/* Document Selection Modals */}
      <RFPDocumentSelectionModal
        isOpen={rfpModalOpen}
        onClose={() => setRfpModalOpen(false)}
        onSelect={(selectedIds) => setStagedDocuments(selectedIds)}
        selectedIds={stagedDocuments}
        proposalDocuments={proposalDocuments}
      />

      <CompanyDocumentSelectionModal
        documentType="past_performance"
        isOpen={ppModalOpen}
        onClose={() => setPPModalOpen(false)}
        onSelect={(selectedIds) => setStagedPastPerformance(selectedIds)}
        selectedIds={stagedPastPerformance}
      />

      <CompanyDocumentSelectionModal
        documentType="resume"
        isOpen={resumeModalOpen}
        onClose={() => setResumeModalOpen(false)}
        onSelect={(selectedIds) => setStagedResumes(selectedIds)}
        selectedIds={stagedResumes}
      />

      <ModelContentSelectionModal
        isOpen={modelContentModalOpen}
        onClose={() => setModelContentModalOpen(false)}
        onSelect={(selectedIds) => setStagedModelContent(selectedIds)}
        selectedIds={stagedModelContent}
      />
    </div>
  );
};

export default SectionChatWithArtifact;